import { Trans } from '@lingui/macro';
import { PhoneCall } from 'lucide-react';
import { useState } from 'react';
import { Room } from '../../models/Room';
import { RoomCall } from '../../models/RoomCall';
import { NotifyUsersOnNewVoiceCall } from '../../models/local/Types';
import { My } from '../../services/My';
import { twil } from '../../services/TwilioHelper';
import LimitedFeature from './LimitedFeature';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover"
import { Button } from "../../components/ui/button"
import { Checkbox } from "../../components/ui/checkbox"
import { Label } from "../../components/ui/label"
import { useToast } from "../../components/ui/use-toast"
import { cn } from "../../lib/utils"

const CallButton: React.FC<{ room: Room, isOngoing: boolean, call: RoomCall | undefined }> = ({ room, isOngoing, call }) => {
  const [inProgress, setInProgress] = useState(false);
  const { toast } = useToast();
  const alreadyConnected = twil.isConnected() && twil.getRoomId() == room.id;
  const meInTheCall = !!(call?.userIds.find(id => id === My.user.id))
  const [notifyMembers, setNotifyMembers] = useState(true);
  const [open, setOpen] = useState(false);

  function canConnect() {
    return !inProgress && !alreadyConnected && !isOngoing;
  }

  async function startCall(notify: NotifyUsersOnNewVoiceCall) {
    try {
      setInProgress(true);
      await twil.connect(room, My.user.id, notify);
    } catch (error) {
      console.error(error);
      toast({
        title: "Error. Could not connect",
        description: (error as Error).message,
        variant: "destructive"
      });
    } finally {
      setInProgress(false);
    }
  }

  function startNewCall() {
    setOpen(false);

    if (!canConnect())
      return;

    setTimeout(() => {
      if (notifyMembers) {
        startCall('notify-all-members')
      } else {
        startCall('notify-none')
      }
    }, 100);
  }

  const StartCallButton = () => (
    <Button
      className="w-full justify-center gap-2 font-semibold h-11 bg-orange-500 hover:bg-orange-600 text-white shadow-sm transition-all rounded-lg"
      onClick={startNewCall}
    >
      <PhoneCall className="w-4 h-4" />
      <Trans>Start a new voice call</Trans>
    </Button>
  );

  return (
    <>
      {canConnect() ? (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="h-10 w-10 data-[state=open]:bg-muted" data-cy="voice-call">
              <img
                src={`assets/icon/headset-mic.svg`}
                style={{ height: isOngoing ? "18px" : "24px" }}
                alt="Voice Call"
              />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2" align="end">
            <div className="flex flex-col gap-1 min-w-[200px]">
              <LimitedFeature
                workspaceId={room.workspaceId}
                tier={'BASIC'}
                content={() => <StartCallButton />}
              />
              <div
                className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-muted rounded-sm select-none"
                onClick={() => setNotifyMembers(!notifyMembers)}
              >
                <Checkbox
                  id="notify-members"
                  checked={notifyMembers}
                  className="pointer-events-none"
                />
                <Label
                  className="text-sm font-medium leading-none cursor-pointer pointer-events-none"
                >
                  <Trans>Notify room members</Trans>
                </Label>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      ) : (
        <span
          className={cn(
            "flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border transition-colors",
            !meInTheCall ? "bg-gray-100 text-gray-500 border-transparent" : "bg-primary/10 text-primary border-primary/20"
          )}
          data-cy="voice-call"
        >
          <img
            src={`assets/icon/ic-groupcall${!meInTheCall ? "-gray" : ""}-18.svg`}
            alt=""
            className="w-[18px] h-[18px]"
          />
          <span>{call?.userIds.length}</span>
        </span>
      )}
    </>
  );
}

export default CallButton;
